import React from "react"

import styled from "styled-components/native"

import rawTokens from "@treefort/tokens/app"

import Icon, { IconProps, IconSize, IconState } from "./icon"
import Text from "./text"
import Touchable, { TouchableProps } from "./touchable"

export type IconButtonProps = {
  source: IconProps["source"]
  label: string
  showLabel?: boolean
  labelPosition?: "below" | "overlaid"
  state?: IconState
  iconSize?: IconSize
  color?: IconProps["color"]
  // Set the minimum size (both width and height) of the icon button to either
  // the minimum recommended tap target size, the height of our regular button
  // (helpful when aligning an icon button with a regular button).
  minSize?: "tapTarget" | "buttonHeight" | "disabled"
  // This can be used to squash the icon button's height independent of the
  // minSize prop. This is helpful on tight mobile layouts, but should be used
  // sparingly to maintain sufficient tap target sizes.
  height?: number
  // This can be used to set a custom width independent of the minimum size
  width?: number
  role?: "button" | "link"
} & TouchableProps

const StyledTouchable = styled(Touchable)`
  align-items: center;
  justify-content: center;
`

const Container = styled.View<{
  $minSize: number
  $height?: number
  $width?: number
}>`
  align-items: center;
  justify-content: center;
  max-width: 100%;
  ${(props) =>
    props.$width === undefined
      ? `min-width: ${props.$minSize}px`
      : `width: ${props.$width}px`};
  ${(props) =>
    props.$height === undefined
      ? `min-height: ${props.$minSize}px`
      : `height: ${props.$height}px`};
`

const Label = styled(Text)<{
  $state: IconState
  $background?: string
}>`
  opacity: ${(props) => props.theme.icon.opacity[props.$state]};
  margin-top: ${(props) => props.theme.iconButton.labelMargin}px;
  border-radius: ${({ theme }) => theme.borderRadius.rounded}px;
`

const OverlaidLabel = styled(Label)`
  position: absolute;
  top: 50%;
  background: ${(props) => props.theme.colors.background.secondary};
`

export default function IconButton({
  source,
  label,
  showLabel = false,
  labelPosition,
  color: colorProp,
  feedback = "opacity",
  state = "default",
  iconSize = "medium",
  minSize: minSizeProp = "tapTarget",
  height,
  width,
  role = "button",
  ...extraProps
}: IconButtonProps): React.JSX.Element {
  const color = extraProps.disabled
    ? "disabled"
    : state === "active"
      ? "accent"
      : colorProp
  const minSize =
    minSizeProp === "disabled"
      ? height || width || 0
      : Math.max(
          minSizeProp === "tapTarget"
            ? rawTokens.minTapTarget
            : rawTokens.button.height.medium,
          rawTokens.icon.size[iconSize],
        )
  const LabelComponent = labelPosition === "overlaid" ? OverlaidLabel : Label

  return (
    <StyledTouchable
      {...extraProps}
      feedback={feedback}
      accessible
      role={role}
      accessibilityState={state === "active" ? { selected: true } : {}}
      aria-selected={state === "active" ? true : undefined}
      aria-label={label}
    >
      {showLabel ? (
        <Container $minSize={minSize} $height={height} $width={width}>
          <Icon
            size={iconSize}
            source={source}
            state={state}
            color={color}
            key={color}
          />
          <LabelComponent
            textStyle="caption"
            numberOfLines={1}
            $state={state}
            key="label"
          >
            {label}
          </LabelComponent>
        </Container>
      ) : (
        <Container $minSize={minSize} $height={height} $width={width}>
          <Icon
            size={iconSize}
            source={source}
            state={state}
            color={color}
            key={color}
          />
        </Container>
      )}
    </StyledTouchable>
  )
}
